import { createContext, ReactNode, useContext, useState } from "react";
import { object, string, boolean } from "yup";
import { useMutation, useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getValidationErrors } from "../../utils/validation/getValidationErrors";
import { PROJECT_CREATE, PROJECT_UPDATE } from "../../graphql/mutations/ProjectMutations";
import { PROJECT_GET, PROJECT_LIST } from "../../graphql/queries/ProjectQueries";
import {
  LeaderboardConfiguration,
  LeaderboardConfigurationUpdateInput,
  Project,
  ProjectStatus,
} from "../../gql/graphql";
import { LEADERBOARD_CONFIGURATION_UPDATE } from "../../graphql/mutations/GameMutation";

export const leaderboardDefaults = {
  header: "Leaderboard",
  headerColor: "#1B1F14",
  subheader: "Check out the top 10 players!",
  subheaderColor: "#4E4F4B",
  showTimer: true,
  numberOfPlayers: 10,
  nicknameQuestion: "What is your nickname?",
  nicknameQuestionColor: "#363635",
  nicknameLabel: "nickname",
  tableNicknameLabel: "nickname",
  tableScoreLabel: "score",
  tableTimeLabel: "time",
  backgroundColor: "#F7E5DB",
};

interface ProjectValidationErrors {
  title?: string;
  publish?: string;
}

interface ProjectContextValue {
  project?: Project;
  settings: {
    title: string;
    publish: boolean;
    setTitle: (title: string) => void;
    setPublish: (publish: boolean) => void;
    directoryId: string;
    setDirectoryId: (id: string) => void;
    hideDatagameBranding: boolean;
    setHideDatagameBranding: (hide: boolean) => void;
    hideSummaryPage: boolean;
    setHideSummaryPage: (hide: boolean) => void;
    showLeaderboard: boolean;
    setShowLeaderboard: (show: boolean) => void;
    leaderboardConfiguration: LeaderboardConfiguration;
    setLeaderboardConfiguration: (config: LeaderboardConfiguration | LeaderboardConfigurationUpdateInput) => void;
  };
  errors: {
    title?: string;
    publish?: string;
  };
  submit: () => Promise<Project | undefined>;
  hasProject: boolean;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const { t: translation } = useTranslation("project");
  const t = (key: string) => translation(`configureProject.form.${key}`);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("id");
  const { data: { project } = {} } = useQuery(PROJECT_GET, {
    variables: {
      projectId,
    },
    skip: !projectId,
    onCompleted: ({ project }) => {
      if (project) {
        setTitle(project.name);
        setPublish(project.status === ProjectStatus.Live);
        setDirectoryId(project.directory.id);
        setHideDatagameBranding(project.hideDatagameBranding);
        setHideSummaryPage(project.hideSummaryPage);
        setShowLeaderboard(project.showLeaderboard);
        setLeaderboardConfiguration(project.leaderboardConfiguration);
      }
    },
  });
  const [title, setTitle] = useState<string>(project?.name ?? "");
  const [directoryId, setDirectoryId] = useState<string>(project?.directoryId ?? "");
  const [publish, setPublish] = useState<boolean>(true);
  const [showLeaderboard, setShowLeaderboard] = useState<boolean>(project?.showLeaderboard ?? false);
  const [leaderboardConfiguration, setLeaderboardConfiguration] = useState(
    project?.leaderboardConfiguration ?? leaderboardDefaults,
  );
  const [hideDatagameBranding, setHideDatagameBranding] = useState<boolean>(project?.hideDatagameBranding ?? false);
  const [hideSummaryPage, setHideSummaryPage] = useState<boolean>(project?.hideSummaryPage ?? false);
  const [errors, setErrors] = useState<ProjectValidationErrors>({});
  const settings = {
    title,
    publish,
    setTitle,
    setPublish,
    directoryId,
    setDirectoryId,
    hideDatagameBranding,
    setHideDatagameBranding,
    hideSummaryPage,
    setHideSummaryPage,
    showLeaderboard,
    setShowLeaderboard,
    leaderboardConfiguration,
    setLeaderboardConfiguration,
  };

  const validationSchema = object({
    title: string().required(t("title.error")),
    publish: boolean().required(t("publish.error")),
  });

  const [createProject] = useMutation(PROJECT_CREATE, {
    refetchQueries: [{ query: PROJECT_LIST }],
  });
  const [editProject] = useMutation(PROJECT_UPDATE);
  const [updateLeaderboardConfig] = useMutation(LEADERBOARD_CONFIGURATION_UPDATE);

  const handleSubmit = async () => {
    const validationErrors = await getValidationErrors<ProjectValidationErrors>({ title, publish }, validationSchema);
    setErrors(validationErrors ?? {});

    if (validationErrors) {
      return;
    }

    // EDIT MODE
    if (projectId) {
      const { data } = await editProject({
        variables: {
          projectId,
          data: {
            name: title,
            directoryId,
            status: publish ? ProjectStatus.Live : ProjectStatus.Draft,
            hideDatagameBranding,
            hideSummaryPage,
            showLeaderboard,
          },
        },
      });
      return data?.projectUpdate;
    } else {
      const { data: { projectCreate } = {} } = await createProject({
        variables: {
          data: {
            name: title,
            status: publish ? ProjectStatus.Live : ProjectStatus.Draft,
            directoryId,
            hideDatagameBranding,
            hideSummaryPage,
            showLeaderboard,
          },
        },
      });

      await updateLeaderboardConfig({
        variables: {
          leaderboardConfigurationId: projectCreate.leaderboardConfiguration.id,
          data: leaderboardConfiguration,
        },
      });

      return projectCreate;
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        settings,
        errors,
        project,
        submit: handleSubmit,
        hasProject: Boolean(project),
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error("useProjectContext must be used within a ProjectProvider");
  }
  return context;
};
